/**
 * CollabReportCard - extracted from FinanceReports.jsx for maintainability.
 * Renders one collaborator card with header (totals + actions) + nested
 * job-by-day table. Also includes the helpers / sub-components used.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { CheckCircle2, MessageCircle, Receipt } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../hooks/useConfirm";
import { buildWhatsappLink } from "../lib/notifyClient";
import {
  exportSingleCollaboratorReceiptPDF,
  fmtBRL,
} from "../lib/financeReportPDF";
import { logError } from "../lib/logger";

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "—";
  }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString("en-US");
  } catch {
    return "—";
  }
}

function PaymentReceiptActions({ row, period, phone, zelle, paidInfo, onPaid }) {
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();

  async function downloadReceipt() {
    try {
      await exportSingleCollaboratorReceiptPDF(row, period);
      toast.success("Recibo gerado");
    } catch (e) {
      toast.error("Erro ao gerar recibo");
      logError(e);
    }
  }

  async function sendWhatsApp() {
    if (!phone) {
      toast.error("Colaborador sem telefone cadastrado");
      return;
    }
    const lines = [
      `Olá ${row.name}! 👋`,
      "",
      `Segue seu recibo de pagamento${period ? ` referente a ${period}` : ""}.`,
      "",
      `💰 *Total a receber:* ${fmtBRL(row.to_receive || 0)}`,
      `📅 *Dias trabalhados:* ${row.days_worked || 0}`,
      `🏠 *Casas atendidas:* ${row.count || 0}`,
      `💵 *Diária:* ${fmtBRL(row.valor_por_dia || 0)}`,
    ];
    if (zelle) lines.push(`💳 *Zelle/Venmo:* ${zelle}`);
    if (row.days_list?.length) {
      lines.push("", "*Detalhamento por dia:*");
      row.days_list.forEach((d) => {
        const date = d.date
          ? new Date(d.date + "T00:00:00").toLocaleDateString("en-US")
          : "—";
        lines.push(
          `• ${date} — ${d.jobs.length} ${d.jobs.length === 1 ? "casa" : "casas"} (${fmtBRL(d.day_total)})`,
        );
      });
    }
    lines.push("", "_Baixe o PDF detalhado em anexo._ 📎");
    const msg = lines.join("\n");
    const link = buildWhatsappLink(phone, msg);
    downloadReceipt();
    window.open(link, "_blank");
    try {
      await api.post("/notification-history", {
        user_id: row.user_id,
        recipient_name: row.name,
        event_key: "payment_receipt",
        channel: "whatsapp",
        message: msg,
      });
    } catch (e) {
      logError("Could not log payment notification:", e);
    }
  }

  async function markAsPaid() {
    if (!(await confirm({
      title: "Registrar pagamento?",
      description: `Valor: ${fmtBRL(row.to_receive || 0)}\nColaborador: ${row.name}\n${row.days_worked || 0} dias · ${row.count || 0} casas\nPeríodo: ${period || "—"}`,
      confirmText: "Sim, registrar",
    }))) return;
    setBusy(true);
    try {
      await api.post("/payments", {
        user_id: row.user_id,
        amount: Number(row.to_receive || 0),
        days_worked: row.days_worked || 0,
        houses: row.count || 0,
        period_label: period || null,
        method: zelle ? "zelle" : "cash",
        notes: `${row.days_worked} dias × ${fmtBRL(row.valor_por_dia)}`,
      });
      toast.success("Pagamento registrado");
      onPaid?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        onClick={downloadReceipt}
        data-testid={`collab-${row.user_id}-receipt-pdf`}
        title="Baixar PDF do recibo deste colaborador"
        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-colors"
      >
        <Receipt size={12} /> PDF
      </button>
      <button
        onClick={sendWhatsApp}
        disabled={!phone}
        data-testid={`collab-${row.user_id}-receipt-whatsapp`}
        title={phone ? "Enviar recibo via WhatsApp" : "Sem telefone cadastrado"}
        className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
          phone
            ? "bg-emerald-600 text-white hover:bg-emerald-700"
            : "bg-slate-100 text-slate-400 cursor-not-allowed"
        }`}
      >
        <MessageCircle size={12} /> WhatsApp
      </button>
      {paidInfo ? (
        <span
          data-testid={`collab-${row.user_id}-paid-badge`}
          title={`Pago em ${new Date(paidInfo.paid_at).toLocaleDateString("en-US")} por ${paidInfo.paid_by_name || "—"}`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200"
        >
          <CheckCircle2 size={12} /> Pago{" "}
          {new Date(paidInfo.paid_at).toLocaleDateString("en-US", { day: "2-digit", month: "2-digit" })}
        </span>
      ) : (
        <button
          onClick={markAsPaid}
          disabled={busy || !row.to_receive}
          data-testid={`collab-${row.user_id}-mark-paid`}
          title="Registrar pagamento"
          className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
            row.to_receive
              ? "bg-amber-500 text-white hover:bg-amber-600"
              : "bg-slate-100 text-slate-400 cursor-not-allowed"
          }`}
        >
          <CheckCircle2 size={12} /> {busy ? "…" : "Marcar pago"}
        </button>
      )}
    </div>
  );
}

function CollabReportCard({ u, info, paidInfo, period, onPaid }) {
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <div className="bg-slate-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200">
        <div className="min-w-0">
          <div className="font-semibold text-slate-900">{u.name}</div>
          <div className="text-xs text-slate-500">
            Diária: <strong>{fmtBRL(u.valor_por_dia)}</strong> · {u.days_worked}{" "}
            {u.days_worked === 1 ? "dia trabalhado" : "dias trabalhados"} ·{" "}
            {u.count} {u.count === 1 ? "casa" : "casas"}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-xs text-slate-500 uppercase tracking-wider">A receber</div>
            <div className="font-bold text-lg text-emerald-700">{fmtBRL(u.to_receive)}</div>
          </div>
          <PaymentReceiptActions
            row={u}
            period={period}
            phone={info.phone}
            zelle={info.zelle_venmo}
            paidInfo={paidInfo}
            onPaid={onPaid}
          />
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-white text-xs uppercase text-slate-500 border-b border-slate-100">
          <tr>
            <th className="text-left p-2.5">Cliente</th>
            <th className="text-left p-2.5">Data</th>
            <th className="text-right p-2.5">Início</th>
            <th className="text-right p-2.5">Fim</th>
            <th className="text-right p-2.5">Total do dia</th>
          </tr>
        </thead>
        <tbody>
          {u.days_list.map((d) => (
            <React.Fragment key={d.date || "no-date"}>
              {d.jobs.map((j, idx) => (
                <tr
                  key={j.job_id}
                  data-testid={`collab-${u.user_id}-job-${j.job_id}`}
                  className="border-b border-slate-100 hover:bg-slate-50"
                >
                  <td className="p-2.5">{j.client_name || "—"}</td>
                  <td className="p-2.5 text-slate-600">{fmtDate(j.date)}</td>
                  <td className="p-2.5 text-right text-slate-600 font-mono">{fmtTime(j.start_time)}</td>
                  <td className="p-2.5 text-right text-slate-600 font-mono">{fmtTime(j.end_time)}</td>
                  <td className="p-2.5 text-right">
                    {idx === d.jobs.length - 1 ? (
                      <strong className="text-emerald-700">{fmtBRL(d.day_total)}</strong>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CollabTable({ rows, period, periodFrom, periodTo, EmptyTable }) {
  const [collabMap, setCollabMap] = useState({});
  const [payments, setPayments] = useState([]);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    api
      .get("/collaborators")
      .then((r) => {
        const m = {};
        r.data.forEach((u) => {
          m[u.id] = { phone: u.phone, zelle_venmo: u.zelle_venmo };
        });
        setCollabMap(m);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (periodFrom) params.set("from", periodFrom);
    if (periodTo) params.set("to", periodTo);
    const qs = params.toString();
    api
      .get(`/payments${qs ? `?${qs}` : ""}`)
      .then((r) => setPayments(r.data))
      .catch(() => setPayments([]));
  }, [periodFrom, periodTo, reload]);

  const paidMap = useMemo(() => {
    const m = {};
    payments.forEach((p) => {
      if (!m[p.user_id] || p.paid_at > m[p.user_id].paid_at) m[p.user_id] = p;
    });
    return m;
  }, [payments]);

  if (!rows.length)
    return <EmptyTable>Sem dados de colaboradores no período.</EmptyTable>;
  return (
    <div className="space-y-6" data-testid="collab-report-table">
      {rows.map((u) => (
        <CollabReportCard
          key={u.user_id}
          u={u}
          info={collabMap[u.user_id] || {}}
          paidInfo={paidMap[u.user_id]}
          period={period}
          onPaid={() => setReload((x) => x + 1)}
        />
      ))}
    </div>
  );
}

export { fmtTime, fmtDate };
